import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

const OTPGenerate = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLeads, setFetchingLeads] = useState(true);
  const [formData, setFormData] = useState({
    leadId: '',
    purpose: 'surgery_verification'
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await api.get('/leads?limit=100&status=confirmed');
      setLeads(res.data.data.leads || []);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setFetchingLeads(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.leadId) {
      toast.error('Please select a lead');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/otp/generate', formData);
      toast.success('OTP generated successfully');
      navigate(`/otp/${res.data.data.otp._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate OTP');
    } finally {
      setLoading(false);
    }
  };

  const selectedLead = leads.find(lead => lead._id === formData.leadId);

  if (fetchingLeads) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Generate OTP</h1>
        <button
          onClick={() => navigate('/otp')}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
        >
          Back to OTP List
        </button>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Lead *
            </label>
            <select
              value={formData.leadId}
              onChange={(e) => setFormData({...formData, leadId: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">Choose a lead...</option>
              {leads.map((lead) => (
                <option key={lead._id} value={lead._id}>
                  {lead.patientName} - {lead.disease} ({lead.phone})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Purpose *
            </label>
            <select
              value={formData.purpose}
              onChange={(e) => setFormData({...formData, purpose: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="surgery_verification">Surgery Verification</option>
              <option value="payment_confirmation">Payment Confirmation</option>
              <option value="identity_verification">Identity Verification</option>
              <option value="follow_up">Follow Up</option>
            </select>
          </div>

          {selectedLead && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Lead Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Patient:</span>
                  <p className="text-gray-900">{selectedLead.patientName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Phone:</span>
                  <p className="text-gray-900">{selectedLead.phone}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Disease:</span>
                  <p className="text-gray-900">{selectedLead.disease}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Urgency:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedLead.urgency === 'high' ? 'bg-red-100 text-red-800' :
                    selectedLead.urgency === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {selectedLead.urgency}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading || !formData.leadId}
              className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'Generating OTP...' : 'Generate OTP'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/otp')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Information Section */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">OTP Information</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• OTP will be valid for 10 minutes</li>
            <li>• OTP can only be used once</li>
            <li>• Patient will receive OTP via SMS</li>
            <li>• OTP is required for surgery verification</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OTPGenerate;
